import { useMemo } from 'react';
import { useResources } from '../../stores';
import { getAttachedResourceIds } from '../entityTypes';
import type { CanvasEntity } from '../entityTypes';
import type { ResourceFieldType, ResourceAggregation } from '../../types';
import { computeAggregate } from '../../lib/formatFieldValue';

export interface AggregatedField {
  fieldId: string;
  key: string;
  type: ResourceFieldType;
  unit?: string;
  interval?: string;
  value: number;
  aggregate: ResourceAggregation;
  count: number;
}

export function useResourceGroupAggregation(
  memberEntityIds: string[],
  allEntities: CanvasEntity[],
): AggregatedField[] {
  const resources = useResources();

  return useMemo(() => {
    // 1. Collect all attached resource IDs from member entities
    const memberEntities = allEntities.filter(e => memberEntityIds.includes(e.id));
    const resourceIdSet = new Set<string>();
    for (const entity of memberEntities) {
      for (const id of getAttachedResourceIds(entity)) resourceIdSet.add(id);
    }

    if (resourceIdSet.size === 0) return [];

    // 2. Build a map of resourceId -> ResourceDefinition
    const resourceMap = new Map(resources.map(r => [r.id, r]));

    // 3. Collect field values grouped by fieldId
    const fieldGroups = new Map<string, {
      key: string;
      type: ResourceFieldType;
      unit?: string;
      interval?: string;
      aggregate: ResourceAggregation;
      values: number[];
    }>();

    for (const resourceId of resourceIdSet) {
      const resource = resourceMap.get(resourceId);
      if (!resource) continue;

      for (const field of resource.fields) {
        if (field.aggregate === 'none' || field.type === 'text') continue;
        if (typeof field.value !== 'number') continue;

        const numValue = field.value;
        if (isNaN(numValue)) continue;

        // Group by key+type so the same field across different resources is aggregated together
        const groupKey = `${field.key}\0${field.type}`;
        const existing = fieldGroups.get(groupKey);
        if (existing) {
          existing.values.push(numValue);
        } else {
          fieldGroups.set(groupKey, {
            key: field.key,
            type: field.type,
            unit: field.unit,
            interval: field.interval,
            aggregate: field.aggregate,
            values: [numValue],
          });
        }
      }
    }

    // 4. Compute aggregated values
    const result: AggregatedField[] = [];
    for (const [groupKey, group] of fieldGroups) {
      const value = computeAggregate(group.aggregate, group.values);
      if (value === undefined) continue;
      result.push({
        fieldId: groupKey,
        key: group.key,
        type: group.type,
        unit: group.unit,
        interval: group.interval,
        value,
        aggregate: group.aggregate,
        count: group.values.length,
      });
    }

    // 5. Sort: currency first, then number, then rest
    result.sort((a, b) => {
      const order: Record<string, number> = { currency: 0, number: 1, percentage: 2, duration: 3, text: 4 };
      return (order[a.type] ?? 99) - (order[b.type] ?? 99);
    });

    return result;
  }, [memberEntityIds, allEntities, resources]);
}

export { formatAggregatedValue } from '../../lib/formatFieldValue';
